import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, CircleDot, Inbox, Loader2, MessageCircle, Plus, Search, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Layout";
import { TicketCard } from "@/components/TicketCard";
import { MyAppointments } from "@/components/calendar/MyAppointments";
import { PRIORITIES } from "@/components/ticketMeta";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const STAT_CARDS = [
  { key: "total", label: "Total", icon: Inbox, status: "all", cls: "text-foreground" },
  { key: "nou", label: "Noi", icon: CircleDot, status: "nou", cls: "text-amber-500" },
  { key: "in_lucru", label: "În lucru", icon: Wrench, status: "in_lucru", cls: "text-blue-500" },
  { key: "rezolvat", label: "Rezolvate", icon: CheckCircle2, status: "rezolvat", cls: "text-emerald-500" },
  { key: "urgente", label: "Urgente", icon: AlertTriangle, status: "urgent", cls: "text-red-500" },
];

const StatsRow = ({ stats, active, onPick }) => (
  <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 sm:gap-4">
    {STAT_CARDS.map((s, i) => (
      <button key={s.key} data-testid={`stat-${s.key}`} onClick={() => onPick(s.status)}
        style={{ animationDelay: `${i * 60}ms`, animationFillMode: "both" }}
        className={cn("rounded-xl border bg-card p-4 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/50 animate-in fade-in slide-in-from-bottom-2",
          active === s.status && "border-primary ring-1 ring-primary", i === 0 && "col-span-2 sm:col-span-1")}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{s.label}</span>
          <s.icon className={cn("h-4 w-4", s.cls)} />
        </div>
        <div className={cn("mt-2 font-display text-3xl font-bold", s.cls)} data-testid={`stat-${s.key}-value`}>{stats?.[s.key] ?? "–"}</div>
      </button>
    ))}
  </div>
);

const WhatsAppBanner = () => (
  <Link to="/nou" data-testid="whatsapp-banner-link"
    className="group mb-6 flex items-center gap-4 rounded-xl border border-green-500/30 bg-green-500/5 p-4 transition-colors hover:bg-green-500/10 dark:bg-emerald-950/40">
    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-green-600 text-white"><MessageCircle className="h-5 w-5" /></div>
    <div className="min-w-0 flex-1">
      <div className="font-semibold">Mesaj nou în grupul „service”?</div>
      <div className="text-sm text-muted-foreground">Copiază-l din WhatsApp și lipește-l aici — AI-ul creează tichetul.</div>
    </div>
    <span className="hidden text-sm font-semibold text-green-600 transition-transform group-hover:translate-x-1 dark:text-green-400 sm:block">Importă →</span>
  </Link>
);

const FilterSelect = ({ value, onChange, options, testId, className }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className={cn("h-10 bg-card", className)}><SelectValue /></SelectTrigger>
    <SelectContent>
      {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const STATUS_TABS = [{ value: "all", label: "Toate" }, { value: "nou", label: "Nou" }, { value: "in_lucru", label: "În lucru" }, { value: "rezolvat", label: "Rezolvat" }];

export default function Dashboard() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [techs, setTechs] = useState([]);
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [tech, setTech] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/users").then((r) => setTechs(r.data));
  }, []);

  useEffect(() => {
    const h = setTimeout(() => {
      const params = {};
      if (status !== "all") params.status = status;
      if (priority !== "all") params.priority = priority;
      if (tech !== "all") params.technician_id = tech;
      if (q.trim()) params.q = q.trim();
      setLoading(true);
      api.get("/tickets", { params }).then((r) => setTickets(r.data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(h);
  }, [status, priority, tech, q]);

  const pickStat = (s) => {
    if (s === "urgent") { setStatus("all"); setPriority("urgenta"); return; }
    setStatus(s);
    setPriority("all");
  };

  const techOptions = [{ value: "all", label: "Toți tehnicienii" }, { value: "me", label: "Ale mele" }, { value: "none", label: "Nealocate" }, ...techs.map((u) => ({ value: u.id, label: u.name }))];

  return (
    <div data-testid="tickets-dashboard">
      <PageHeader title="Tichete service" subtitle={`Salut, ${user.name.split(" ")[0]}! Ai ${stats?.ale_mele ?? 0} tichete active alocate.`}>
        <Button asChild className="hidden sm:inline-flex" data-testid="dashboard-new-ticket-button">
          <Link to="/nou"><Plus className="h-4 w-4" /> Tichet nou</Link>
        </Button>
      </PageHeader>
      <MyAppointments />
      <StatsRow stats={stats} active={priority === "urgenta" ? "urgent" : status} onPick={pickStat} />
      <WhatsAppBanner />

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex gap-1 overflow-x-auto rounded-lg border bg-card p-1" data-testid="status-tabs">
          {STATUS_TABS.map((t) => (
            <button key={t.value} data-testid={`status-tab-${t.value}`} onClick={() => setStatus(t.value)}
              className={cn("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                status === t.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="tickets-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută client, telefon, echipament, cod..." className="h-10 bg-card pl-9" />
        </div>
        <div className="grid grid-cols-2 gap-3 lg:flex">
          <FilterSelect testId="priority-filter" value={priority} onChange={setPriority} options={[{ value: "all", label: "Toate prioritățile" }, ...PRIORITIES]} className="lg:w-44" />
          <FilterSelect testId="technician-filter" value={tech} onChange={setTech} options={techOptions} className="lg:w-48" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : tickets.length === 0 ? (
        <div className="rounded-xl border border-dashed py-16 text-center" data-testid="tickets-empty">
          <Inbox className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 font-semibold">Niciun tichet găsit</p>
          <p className="text-sm text-muted-foreground">Adaugă primul tichet sau schimbă filtrele.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="tickets-list">
          {tickets.map((t, i) => <TicketCard key={t.id} t={t} index={i} />)}
        </div>
      )}
    </div>
  );
}
